/**
 * Billing settings page for managing subscriptions, payment methods, and auto top-up.
 */

import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Elements } from "@stripe/react-stripe-js";
import {
  CreditCard,
  Crown,
  Trash2,
  Star,
  Loader2,
  Plus,
  AlertCircle,
} from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { callable } from "../lib/callable";
import { getStripe } from "../lib/stripe";
import { formatPrice } from "../lib/pricing";
import { usePayment } from "../lib/PaymentContext";
import { getPricingPlans } from "../services/naraDoor";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import BoostBalance from "./BoostBalance";
import AutoTopUpSettings from "./AutoTopUpSettings";
import PaymentMethodForm from "./PaymentMethodForm";
import Header from "./Header";
import BillingTabs from "./BillingTabs";

interface PaymentMethodInfo {
  id: string;
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
  isDefault: boolean;
}

export default function BillingSettings() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { formatDate } = useDateFormatter();
  const {
    subscriptionTier,
    subscriptionPriceId,
    subscriptionStatus,
    currentPeriodEnd,
    cancelAtPeriodEnd,
    cancelSubscription,
    reactivateSubscription,
    createSetupIntent,
    setDefaultPaymentMethod,
    removePaymentMethod,
    refreshStatus,
  } = usePayment();

  const { data: pricingData, error: pricingError } = useQuery({
    queryKey: ["pricingPlans"],
    queryFn: getPricingPlans,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
  const subscriptionPlans = pricingData?.subscriptions ?? [];

  const [showAddCard, setShowAddCard] = useState(false);
  const [setupClientSecret, setSetupClientSecret] = useState<string | null>(null);
  const [isSettingUp, setIsSettingUp] = useState(false);
  const [isCanceling, setIsCanceling] = useState(false);
  const [isReactivating, setIsReactivating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getPaymentMethodsFn = callable<
    void,
    { paymentMethods: PaymentMethodInfo[]; defaultPaymentMethodId: string | null }
  >("getPaymentMethods");

  const methodsQuery = useQuery({
    queryKey: ["paymentMethods"],
    queryFn: async () => {
      try {
        return (await getPaymentMethodsFn()).data.paymentMethods;
      } catch (err) {
        console.error("Failed to load payment methods:", err);
        throw err;
      }
    },
  });
  const paymentMethods = methodsQuery.data ?? [];
  const isLoadingMethods = methodsQuery.isLoading;

  // A card with no default becomes the default; the write's refresh then shows it.
  useEffect(() => {
    const methods = methodsQuery.data;
    if (methods && methods.length > 0 && !methods.some((pm) => pm.isDefault)) {
      setDefaultPaymentMethod(methods[0].id).catch((err) => {
        console.error("Failed to auto-set default payment method:", err);
      });
    }
  }, [methodsQuery.data]);

  // Handle setup completion from redirect
  useEffect(() => {
    if (searchParams.get("setup") === "complete") {
      refreshStatus();
      methodsQuery.refetch();
      navigate("/billing", { replace: true });
    }
  }, [searchParams]);

  const handleAddCard = async () => {
    setIsSettingUp(true);
    setError(null);
    try {
      const result = await createSetupIntent();
      setSetupClientSecret(result.clientSecret);
      setShowAddCard(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("billing.errors.initializeCardSetup"));
    } finally {
      setIsSettingUp(false);
    }
  };

  const handleCardSaved = async () => {
    setShowAddCard(false);
    setSetupClientSecret(null);

    // Stripe saved the card without a callable, so nothing refreshed the list yet.
    await methodsQuery.refetch();
    refreshStatus();
  };

  const handleSetDefault = async (paymentMethodId: string) => {
    try {
      await setDefaultPaymentMethod(paymentMethodId);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("billing.errors.setDefault"));
    }
  };

  const handleRemove = async (paymentMethodId: string) => {
    if (!confirm(t("billing.confirmRemoveCard"))) return;

    try {
      await removePaymentMethod(paymentMethodId);
      refreshStatus();
    } catch (err) {
      setError(err instanceof Error ? err.message : t("billing.errors.removeCard"));
    }
  };

  const handleCancelSubscription = async () => {
    if (!confirm(t("billing.confirmCancelSubscription")))
      return;

    setIsCanceling(true);
    try {
      await cancelSubscription();
    } catch (err) {
      setError(err instanceof Error ? err.message : t("billing.errors.cancel"));
    } finally {
      setIsCanceling(false);
    }
  };

  const handleReactivate = async () => {
    setIsReactivating(true);
    try {
      await reactivateSubscription();
    } catch (err) {
      setError(err instanceof Error ? err.message : t("billing.errors.reactivate"));
    } finally {
      setIsReactivating(false);
    }
  };

  const shownError = error ?? (methodsQuery.isError ? t("billing.errors.loadPaymentMethods") : null);

  const currentTierInfo = subscriptionPriceId
    ? subscriptionPlans.find((tier) => tier.priceId === subscriptionPriceId)
    : subscriptionPlans.find((tier) => tier.priceId === "free");

  const formatCardBrand = (brand: string) => {
    const brands: Record<string, string> = {
      visa: "Visa",
      mastercard: "Mastercard",
      amex: "American Express",
      discover: "Discover",
    };
    return brands[brand] || brand.charAt(0).toUpperCase() + brand.slice(1);
  };

  return (
    <div className="min-h-screen bg-muted">
      <Header />

      <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        <h1 className="text-2xl font-bold">{t("billing.title")}</h1>

        <BillingTabs />

        {shownError && (
          <div className="p-4 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg text-red-600 flex items-start gap-2">
            <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
            <span>{shownError}</span>
          </div>
        )}

        {pricingError && (
          <div className="p-4 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg text-red-600 flex items-start gap-2">
            <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
            <span>{t("pricing.errors.loadPricing", "Failed to load pricing plans. Please try again later.")}</span>
          </div>
        )}

        {/* Boost Balance */}
        <div className="bg-card rounded-xl border p-6">
          <h2 className="font-semibold mb-4">{t("billing.boostBalance")}</h2>
          <BoostBalance showPurchaseButton onPurchaseClick={() => navigate("/pricing")} />
        </div>

        {/* Current Subscription */}
        <div className="bg-card rounded-xl border p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Crown className="h-5 w-5 text-yellow-500" />
              <h2 className="font-semibold">{t("billing.subscription")}</h2>
            </div>
            <button
              onClick={() => navigate("/pricing")}
              className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-300 font-medium"
            >
              {t("billing.changePlan")}
            </button>
          </div>

          <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
            <div>
              <p className="font-medium">{currentTierInfo?.name || t("billing.freePlan")} {t("billing.plan")}</p>
              <p className="text-sm text-muted-foreground">
                {t("billing.boostsPerMonth", { value: currentTierInfo?.boosts.toLocaleString() })}
              </p>
              {subscriptionStatus === "active" && currentPeriodEnd && (
                <p className="text-sm text-muted-foreground mt-1">
                  {cancelAtPeriodEnd ? t("billing.cancelsOn") : t("billing.renewsOn")}{" "}
                  {formatDate(currentPeriodEnd, "long-date")}
                </p>
              )}
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold">
                {currentTierInfo && formatPrice(currentTierInfo.unitAmount, currentTierInfo.currency)}
              </p>
              {(currentTierInfo?.unitAmount ?? 0) > 0 && (
                <p className="text-sm text-muted-foreground">{t("billing.perMonth")}</p>
              )}
            </div>
          </div>

          {subscriptionStatus === "active" && subscriptionTier !== "free" && (
            <div className="mt-4">
              {cancelAtPeriodEnd ? (
                <button
                  onClick={handleReactivate}
                  disabled={isReactivating}
                  className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-300 font-medium flex items-center gap-2"
                >
                  {isReactivating && <Loader2 className="h-4 w-4 animate-spin" />}
                  {t("billing.reactivateSubscription")}
                </button>
              ) : (
                <button
                  onClick={handleCancelSubscription}
                  disabled={isCanceling}
                  className="text-sm text-red-600 hover:text-red-700 dark:text-red-300 font-medium flex items-center gap-2"
                >
                  {isCanceling && <Loader2 className="h-4 w-4 animate-spin" />}
                  {t("billing.cancelSubscription")}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Payment Methods */}
        <div className="bg-card rounded-xl border p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-muted-foreground" />
              <h2 className="font-semibold">{t("billing.paymentMethods")}</h2>
            </div>
            <button
              onClick={handleAddCard}
              disabled={isSettingUp}
              className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-300 font-medium flex items-center gap-1"
            >
              {isSettingUp ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
              {t("billing.addCard")}
            </button>
          </div>

          {showAddCard && setupClientSecret && (
            <div className="mb-4 p-4 border rounded-lg">
              <h3 className="font-medium mb-3">{t("billing.addNewCard")}</h3>
              <Elements
                stripe={getStripe()}
                options={{
                  clientSecret: setupClientSecret,
                  appearance: {
                    theme: "stripe",
                    variables: { colorPrimary: "#2563eb" },
                  },
                }}
              >
                <PaymentMethodForm
                  onSuccess={handleCardSaved}
                  onError={setError}
                />
              </Elements>
              <button
                onClick={() => {
                  setShowAddCard(false);
                  setSetupClientSecret(null);
                }}
                className="mt-3 text-sm text-muted-foreground hover:text-foreground"
              >
                {t("common.cancel")}
              </button>
            </div>
          )}

          {isLoadingMethods ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : paymentMethods.length === 0 ? (
            <p className="text-muted-foreground text-sm py-4">
              {t("billing.noPaymentMethods")}
            </p>
          ) : (
            <div className="space-y-3">
              {paymentMethods.map((pm) => (
                <div
                  key={pm.id}
                  className="flex items-center justify-between p-3 border rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <CreditCard className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium">
                        {t("billing.cardEndingIn", { brand: formatCardBrand(pm.brand), last4: pm.last4 })}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {t("billing.expires", { month: pm.expMonth, year: pm.expYear })}
                      </p>
                    </div>
                    {pm.isDefault && (
                      <span className="flex items-center gap-1 text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded-full">
                        <Star className="h-3 w-3" />
                        {t("billing.default")}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {!pm.isDefault && (
                      <>
                        <button
                          onClick={() => handleSetDefault(pm.id)}
                          className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-300"
                        >
                          {t("billing.setDefault")}
                        </button>
                        <button
                          onClick={() => handleRemove(pm.id)}
                          className="p-1 text-muted-foreground hover:text-red-600"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Auto Top-Up */}
        <AutoTopUpSettings
          onAddPaymentMethod={handleAddCard}
          hasPaymentMethod={paymentMethods.some(pm => pm.isDefault)}
        />
      </div>
    </div>
  );
}
